import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import { useAuth } from '../../contexts/AuthContext'
import QuestionRenderer from '../QuestionRenderer'
import GroupedBreakdown from '../results/GroupedBreakdown'
import { Button } from '../ui'
import { buildDemoQuestions } from '../../data/demo-survey'
import type { GroupResult } from '../../types'

type DemoState = 'intro' | 'questions' | 'results'

const GROUP_META: Record<string, { nameId: string; color: string }> = {
  confiance: { nameId: 'marketing.demo.group.confidence', color: 'blue' },
  quotidien: { nameId: 'marketing.demo.group.daily', color: 'green' },
}

const DemoSurvey: React.FC = () => {
  const { user } = useAuth()
  const intl = useIntl()
  const DEMO_QUESTIONS = useMemo(() => buildDemoQuestions(intl), [intl])
  const [state, setState] = useState<DemoState>('intro')
  const [answers, setAnswers] = useState<Record<string, string | number | boolean>>({})
  const [currentQ, setCurrentQ] = useState(0)

  const handleAnswer = (questionId: string, value: string | number | boolean) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }))
  }

  const handleNext = () => {
    if (currentQ < DEMO_QUESTIONS.length - 1) {
      setCurrentQ(currentQ + 1)
    } else {
      setState('results')
    }
  }

  const handleBack = () => {
    if (currentQ > 0) setCurrentQ(currentQ - 1)
  }

  const handleReset = () => {
    setState('intro')
    setAnswers({})
    setCurrentQ(0)
  }

  const buildResults = (): { groups: GroupResult[]; overall: number } => {
    const groups: GroupResult[] = []
    let totalScore = 0
    let totalMax = 0

    for (const q of DEMO_QUESTIONS) {
      const val = answers[q.id]
      let score = 0
      let maxScore = 0

      if (q.type === 'RATING' && val != null) {
        score = Number(val)
        maxScore = 5
      } else if (q.type === 'BOOLEAN' && val != null) {
        score = val === true ? 1 : 0
        maxScore = 1
      } else {
        continue
      }

      totalScore += score
      totalMax += maxScore

      const groupId = q.group_id || q.id
      const metaConfig = GROUP_META[groupId]
      const meta = metaConfig
        ? { name: intl.formatMessage({ id: metaConfig.nameId }), color: metaConfig.color }
        : { name: q.text, color: 'blue' }

      groups.push({
        group_id: groupId,
        group_name: meta.name,
        color: meta.color,
        total_score: score,
        max_possible_score: maxScore,
        percentage: maxScore > 0 ? Math.round((score / maxScore) * 100) : 0,
        question_scores: [{
          question_id: q.id,
          category: meta.name,
          score,
          max_possible_score: maxScore,
          percentage: maxScore > 0 ? Math.round((score / maxScore) * 100) : 0,
        }],
      })
    }

    const overall = totalMax > 0 ? Math.round((totalScore / totalMax) * 100) : 0
    return { groups, overall }
  }

  const question = DEMO_QUESTIONS[currentQ]
  const hasAnswer = answers[question?.id] != null

  return (
    <div id="demo" className="max-w-3xl">
      <h3 className="text-xl sm:text-2xl font-bold font-heading text-foreground">
        {intl.formatMessage({ id: 'marketing.demo.title' })}
      </h3>
      <p className="mt-1 mb-4 text-sm text-foreground-muted">
        {intl.formatMessage({ id: 'marketing.demo.subtitle' })}
      </p>

      <div className="bg-surface rounded-2xl border border-border-light shadow-lg overflow-hidden">
        {/* Intro */}
        {state === 'intro' && (
          <div className="p-4 sm:p-8 text-center">
            <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-primary/10 text-primary mb-4">
              <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15a2.25 2.25 0 012.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V18.75m-7.5-10.5h6.375c.621 0 1.125.504 1.125 1.125v9.375m-8.25-3l1.5 1.5 3-3.75" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-foreground mb-2"><FormattedMessage id="marketing.demo.surveyName" /></h3>
            <div className="flex justify-center gap-4 text-sm text-foreground-muted mb-6">
              <span><FormattedMessage id="marketing.demo.questionCount" values={{ count: DEMO_QUESTIONS.length }} /></span>
              <span>&middot;</span>
              <span><FormattedMessage id="marketing.demo.duration" /></span>
            </div>
            <Button variant="primary" size="lg" onClick={() => setState('questions')}>
              <FormattedMessage id="marketing.demo.tryIt" />
            </Button>
          </div>
        )}

        {/* Questions */}
        {state === 'questions' && question && (
          <div className="p-4 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex-1 h-2 bg-background-alt rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all duration-300"
                  style={{ width: `${((currentQ + 1) / DEMO_QUESTIONS.length) * 100}%` }}
                />
              </div>
              <span className="text-xs text-foreground-muted">
                {currentQ + 1}/{DEMO_QUESTIONS.length}
              </span>
            </div>

            <div className="mb-2">
              <h3 className="text-lg font-semibold text-foreground mb-1">
                {question.text}
              </h3>
              {question.required && (
                <span className="text-xs text-error"><FormattedMessage id="marketing.demo.required" /></span>
              )}
            </div>

            <div className="my-6">
              <QuestionRenderer
                question={question}
                value={answers[question.id]}
                onChange={(val) => handleAnswer(question.id, val)}
                ratingLabels={question.rating_labels}
              />
            </div>

            <div className="flex justify-between">
              <Button variant="ghost" onClick={handleBack} disabled={currentQ === 0}>
                <FormattedMessage id="marketing.demo.back" />
              </Button>
              <Button variant="primary" onClick={handleNext} disabled={question.required && !hasAnswer}>
                {currentQ === DEMO_QUESTIONS.length - 1
                  ? <FormattedMessage id="marketing.demo.seeResults" />
                  : <FormattedMessage id="marketing.demo.next" />}
              </Button>
            </div>
          </div>
        )}

        {/* Results */}
        {state === 'results' && (() => {
          const { groups, overall } = buildResults()
          return (
          <div className="p-4 sm:p-8">
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold text-foreground"><FormattedMessage id="marketing.demo.resultsTitle" /></h3>
              <p className="text-sm text-foreground-muted mt-1">
                <FormattedMessage id="marketing.demo.resultsSubtitle" />
              </p>
            </div>

            <GroupedBreakdown groups={groups} overallPercentage={overall} lang={intl.locale} />

            <div className="mt-8 text-center space-y-3">
              <div>
                <Link to={user ? '/surveys/new' : '/auth/signup'}>
                  <Button variant="primary" size="lg"><FormattedMessage id="marketing.demo.createYourOwn" /></Button>
                </Link>
              </div>
              <button
                onClick={handleReset}
                className="text-sm text-foreground-muted hover:text-foreground underline"
              >
                <FormattedMessage id="marketing.demo.tryAgain" />
              </button>
            </div>
          </div>
          )
        })()}
      </div>
    </div>
  )
}

export default DemoSurvey
